import { createSelector } from 'reselect';
import { CashModel, CurrencyModel, KassaModel } from '@/interface';
import { StateReducer } from '../reducer';
import { filterDepartmentsByRole, getRateChannel, RateChannel } from '@/core/helpers/PageHelpers';

const selectRolesData = (state: StateReducer) => state?.header?.rolesData;
const selectRawDepartmentList = (state: StateReducer) => state.departmentReducer.departmentList;

// Ограничение по роли применяем и на чтении: даже если в стор по какой-то причине
// попал более широкий список (гонка сокетов, лендинг, смена пользователя в одной вкладке),
// пользователь всё равно не увидит чужие отделения.
export const selectDepartmentList = createSelector(
    [selectRawDepartmentList, selectRolesData],
    (departmentList, rolesData) => filterDepartmentsByRole(departmentList, rolesData)
);

const CHANNEL_ORDER = [RateChannel.Mobile, RateChannel.Website];

/**
 * Отделения-каналы с курсами мобилки и ecash.kz. В общих списках их нет —
 * берут только экраны, которым они нужны: курсы казначея и распоряжения.
 */
export const selectChannelDepartments = createSelector(
    [(state: StateReducer) => state.departmentReducer.channelDepartments, selectRolesData],
    (channelDepartments, rolesData) => filterDepartmentsByRole(channelDepartments, rolesData)
        .slice()
        .sort((x, y) => CHANNEL_ORDER.indexOf(getRateChannel(x) as RateChannel) - CHANNEL_ORDER.indexOf(getRateChannel(y) as RateChannel))
);

/** Отделения страницы курсов: каналы впереди, чтобы в группе города они шли первыми */
export const selectRatesDepartmentList = createSelector(
    [selectChannelDepartments, selectDepartmentList],
    (channelDepartments, departmentList) => [...channelDepartments, ...departmentList]
);

const selectIsCashier =(state: StateReducer) => state?.header?.rolesData?.isCashier;
const selectAllDepartments = (state: StateReducer) => state?.departmentReducer?.allDepartments;
const selectInitialDepartments = createSelector(
    [(state: StateReducer) => state?.departmentReducer?.initialDepartments, selectRolesData],
    (initialDepartments, rolesData) => filterDepartmentsByRole(initialDepartments, rolesData)
);
const selectInitialOpenDepartments = createSelector(
    [(state: StateReducer) => state?.departmentReducer?.initialOpenDepartments, selectRolesData],
    (initialOpenDepartments, rolesData) => filterDepartmentsByRole(initialOpenDepartments, rolesData)
);
const selectActiveDepartmentId = (state: StateReducer) => state?.departmentReducer?.activeDepartmentId;
const selectActiveKassaId = (state: StateReducer) => state?.departmentReducer?.activeKassaId;
const selectActiveDepartmentLandingAddress = (state: StateReducer) => state?.departmentReducer?.activeDepartmentLandingAddress;


export const selectDepartmentListLanding = (state: StateReducer) => state.departmentReducer.departmentListLanding;
export const selectActiveRegion = (state: StateReducer) => state.departmentReducer.activeRegion;
export const selectNewCurrency = (state: StateReducer) => state.departmentReducer.newCurrency;
export const selectActiveCurrencyId = (state: StateReducer, currencyList: CurrencyModel[]) => currencyList?.find(cur => cur?.currencyId === state?.departmentReducer?.activeCurrencyId);
export const selectAllInitialDepartments = createSelector(
    [selectIsCashier, selectInitialDepartments, selectActiveDepartmentId],
    (isCashier, allDepartments, activeDepartmentId) => {
        if (!isCashier) {
            return allDepartments;
        }
        const dep = allDepartments?.find(dep => dep?.id === activeDepartmentId);
        return dep ? [dep] : [];
    }
);
export const selectAllInitialOpenDepartments = createSelector(
    [selectIsCashier, selectInitialOpenDepartments, selectActiveDepartmentId],
    (isCashier, initialOpenDepartments, activeDepartmentId) => {
        if (!isCashier) {
            return initialOpenDepartments;
        }
        const dep = initialOpenDepartments?.find(dep => dep?.id === activeDepartmentId);
        return dep ? [dep] : [];
    }
);
// Список для выпадашки выбора отделения — уже ограничен ролью.
export const selectSelectableDepartments = selectInitialDepartments;
export const selectActiveDepartment = createSelector(
    [selectAllDepartments, selectActiveDepartmentId],
    (allDepartments, activeDepartmentId) => allDepartments?.find(dep => dep?.id === activeDepartmentId)
);
export const selectActiveDepartmentLanding = createSelector(
    [selectDepartmentListLanding, selectActiveDepartmentLandingAddress],
    (departmentListLanding, activeDepartmentLandingAddress) => departmentListLanding?.find((dep) => dep?.address === activeDepartmentLandingAddress)
);

export const selectActiveKassa = createSelector(
    [selectActiveDepartment, selectActiveKassaId],
    (activeDepartment, activeKassaId) => activeDepartment?.kassa?.find((kassa: KassaModel) => kassa?.id === activeKassaId)
);
export const selectCashObj = (cashList: CashModel[]) => {
    const cashObj: Record<string, CashModel> = {};
    cashList?.forEach(cash => cashObj[cash?.currencyCode] = cash);
    return cashObj;
};
export const selectKassaObj = (kassaList: KassaModel[]) => {
    const kassaObj: Record<number, KassaModel> = {};
    kassaList?.forEach(kassa => kassaObj[kassa?.id] = kassa);
    return kassaObj;
};